/**
 * LookoutWarehouseEditor — set or clear a saved Snowflake connection's Lookout compute warehouse
 * (ACT2-1031).
 *
 * Shown in the account view for every Snowflake connection, local or created in RudderStack: the value
 * is Lookout's alone (it only changes which warehouse Lookout dials), so it is editable even where the
 * credential is not. Clearing the field and saving removes the override, and Lookout goes back to the
 * compute warehouse. The server tears down the connection's open sessions on every change.
 *
 * A new name is shown with the one grant it needs, and the server refuses the save unless Lookout can
 * run a test query on it: Snowflake accepts a login on a warehouse the role cannot use, and every
 * Lookout query would then fail with "No active warehouse selected".
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { CopyButton } from '@/_shared/components/CopyButton';
import { Button } from '@/_shared/components/ui/button';
import { Form, FormField } from '@/_shared/components/ui/form';
import { Input } from '@/_shared/components/ui/input';
import { WarehouseProblemCallout } from '@/_shared/components/WarehouseProblemCallout/WarehouseProblemCallout';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { useSyntaxStyle } from '@/_shared/hooks/useSyntaxStyle';
import { codeBlockCustomStyle, SyntaxHighlighter } from '@/_shared/lib/syntax';

import { lookoutWarehouseGrantSQL } from '../../../lib/snowflake-setup';
import { refreshPermissionOnRefusal } from '../accountCreatePermission';
import { refusalMessage, refusalProblem } from '../refusalCopy';
import { FormSection } from '../shared/FormSection';
import { OutputSection } from '../shared/OutputSection';
import { LOOKOUT_WAREHOUSE_HINT, optionalWarehouseNameSchema } from './lookoutWarehouseField';

const SchemaModel = z.object({ lookoutWarehouse: optionalWarehouseNameSchema });
type Values = z.infer<typeof SchemaModel>;

interface LookoutWarehouseEditorProps {
  connectionId: string;
  /** The stored value; null when Lookout uses the compute warehouse. */
  lookoutWarehouse: string | null;
  /** The compute warehouse reverse ETL uses, shown so the admin knows what Lookout falls back to. */
  computeWarehouse: string | null;
  /** The connection's service role, for the grant line; `<ROLE>` is shown when unknown. */
  role?: string | null;
  /** ACT2-1276: why the RudderStack permission blocks the change, or null. */
  blockedReason: string | null;
}

export function LookoutWarehouseEditor({
  connectionId,
  lookoutWarehouse,
  computeWarehouse,
  role = null,
  blockedReason,
}: LookoutWarehouseEditorProps): React.JSX.Element {
  const orpc = useOrpc();
  const syntaxStyle = useSyntaxStyle();
  const queryClient = useQueryClient();
  const form = useForm<Values>({
    resolver: zodResolver(SchemaModel),
    defaultValues: { lookoutWarehouse: lookoutWarehouse ?? '' },
  });
  const watched = useWatch({ control: form.control, name: 'lookoutWarehouse' });
  // Only for a name the admin is about to save: the stored one already has its grant.
  const grant = watched.trim() !== (lookoutWarehouse ?? '') ? lookoutWarehouseGrantSQL(watched, role) : null;
  // A save elsewhere (or a refetch) moves the stored value; follow it unless the admin is mid-edit.
  React.useEffect(() => {
    if (!form.formState.isDirty) form.reset({ lookoutWarehouse: lookoutWarehouse ?? '' });
  }, [form, lookoutWarehouse]);

  const save = useMutation(
    orpc.settings.warehouses.setLookoutWarehouse.mutationOptions({
      onSuccess: (result) => {
        void queryClient.invalidateQueries({ queryKey: orpc.settings.warehouses.key() });
        form.reset({ lookoutWarehouse: result.lookoutWarehouse ?? '' });
        toast.success(
          result.lookoutWarehouse
            ? `Lookout now uses ${result.lookoutWarehouse}`
            : 'Lookout now uses the compute warehouse',
        );
      },
      // A refused test query comes with its problem (ACT2-1077), shown under the field; anything else
      // is a toast.
      onError: (err) => {
        refreshPermissionOnRefusal(queryClient, orpc, err);
        if (!refusalProblem(err)) toast.error(refusalMessage(err, 'Could not save the Lookout compute warehouse'));
      },
    }),
  );
  const saveProblem = save.isError ? refusalProblem(save.error) : undefined;

  const onSubmit = form.handleSubmit((values) => {
    save.mutate({ connectionId, lookoutWarehouse: values.lookoutWarehouse.trim() || null });
  });

  const description = computeWarehouse
    ? `${LOOKOUT_WAREHOUSE_HINT} Empty means ${computeWarehouse}.`
    : LOOKOUT_WAREHOUSE_HINT;

  return (
    <Form {...form}>
      <form onSubmit={(e) => void onSubmit(e)} className="mt-6 grid gap-3" noValidate>
        <FormField
          control={form.control}
          name="lookoutWarehouse"
          render={({ field }) => (
            <FormSection label="Lookout compute warehouse" optional description={description}>
              <Input {...field} placeholder="LOOKOUT_WH" disabled={blockedReason !== null || save.isPending} />
            </FormSection>
          )}
        />
        {grant && (
          <OutputSection
            label="Run first"
            description="Lets the service role use this warehouse. Lookout tests it before saving."
            action={<CopyButton text={grant} label="Copy" copyLabel="Grant" />}
          >
            <SyntaxHighlighter language="sql" style={syntaxStyle} customStyle={codeBlockCustomStyle}>
              {grant}
            </SyntaxHighlighter>
          </OutputSection>
        )}
        {saveProblem && <WarehouseProblemCallout problem={saveProblem} />}
        <ActionTooltip show={blockedReason !== null} message={blockedReason ?? ''} className="justify-self-end">
          <Button
            type="submit"
            variant="secondary"
            className="justify-self-end"
            label="Save"
            loading={save.isPending}
            disabled={blockedReason !== null || !form.formState.isDirty}
          />
        </ActionTooltip>
      </form>
    </Form>
  );
}
